/**
 * Central palette for the 2D canvas renderer. Previously these hex values
 * were scattered throughout `renderer.ts` — consolidating them here makes
 * it possible to re-skin the editor in one place and opens the door for
 * user-configurable themes later.
 *
 * Only visual concerns live here. Object dimensions, geometry, and hit-test
 * sizes stay where they're used.
 */

/** Canvas chrome — grid, rulers, stage boundary, selection affordances. */
export const CHROME = {
  grid: '#e5e7eb',
  gridMajor: '#d1d5db',
  gridText: '#6b7280',
  stageFill: '#fafaf9',
  stageBorder: '#6b7280',
  stageTicks: '#9ca3af',
  stageLabel: '#374151',
  selected: '#3b82f6',
  hover: '#60a5fa',
  alignmentGuide: '#22c55e',
  text: '#000000',
} as const;

/** Walls + barrier defaults (2D top-down). */
export const WALL = {
  fill: '#6b7280',
  mesh: '#9ca3af',
} as const;

/** Shooting positions (start position, shooting box, free-fire zone). */
export const POSITION = {
  fill: '#22c55e',
  border: '#16a34a',
} as const;

/** IDPA-style paper silhouette. */
export const PAPER = {
  body: '#c4a265',
  outline: '#8b7355',
} as const;

/** No-shoot uses the same silhouette plus a blue overlay cross. */
export const NO_SHOOT = {
  body: '#c4a265',
  outline: '#8b7355',
  mark: '#3b82f6',
} as const;

/** Steel plates (round_8 / 10 / 12) and generic plate fallback. */
export const STEEL = {
  plate: '#9ca3af',
  outline: '#000000',
} as const;

/** Popper — head + body in brushed steel, darker base. */
export const POPPER = {
  body: '#999999',
  bodyOutline: '#666666',
  base: '#555555',
} as const;

/** 55-gal barrel — saddle brown body, dark top/bottom rim, bung-hole. */
export const BARREL = {
  body: '#8b4513',
  rim: '#3d2410',
} as const;

/** Table — dark tabletop with legs. */
export const TABLE = {
  top: '#1f2937',
  edge: '#0b1220',
  seam: '#4b5563',
  leg: '#0b1220',
} as const;

/** Furniture props — chair, door, window, bed, couch. */
export const FURNITURE = {
  wood: '#a87339',
  woodDark: '#6b4423',
  fabric: '#5b7c99',
  fabricDark: '#3a566e',
  mattress: '#e5e7eb',
  pillow: '#f9fafb',
  outline: '#374151',
  glass: '#9cc3d5',
} as const;

/** Vehicle prop — sedan top-down / 3D body. */
export const CAR = {
  body: '#b91c1c',
  roof: '#7f1d1d',
  glass: '#9cc3d5',
  tire: '#1f1f1f',
  outline: '#450a0a',
} as const;

/** Traffic cone top-down. */
export const CONE = {
  body: '#fb923c',
  outline: '#9a3412',
  stripe: '#ffffff',
} as const;

/** Fault line — thin red stripe. */
export const FAULT_LINE = {
  fill: '#ef4444',
} as const;

/** Hard cover — opaque panel. */
export const HARD_COVER = {
  fill: '#4b5563',
  outline: '#1f2937',
} as const;

/** Soft cover — see-through mesh. */
export const SOFT_COVER = {
  fill: '#a8a29e',
  outline: '#78716c',
} as const;

/** Steel plate on a stand (separate from the flat steel target). */
export const PLATE = {
  plate: '#9ca3af',
  outline: '#333333',
  stem: '#555555',
} as const;

/** Swinger / drop turner / texas star / bobber share a minimal palette. */
export const MOVING = {
  body: '#9ca3af',
  outline: '#333333',
  armLight: '#888888',
  armDark: '#444444',
  arrow: '#1f2937',
} as const;

/** Generic orange fallback for placeholder object types. */
export const FALLBACK = {
  fill: '#f97316',
  outline: '#000000',
} as const;

/** Shooter marker (top-down) — blueprint symbol with pose hints. */
export const SHOOTER = {
  fill: '#e0f2fe',
  outline: '#075985',
  accent: '#0ea5e9',
  text: '#ffffff',
  chairWood: '#6b4423',
  chairDark: '#3d2410',
  broomStick: '#92400e',
  broomBristle: '#a97c50',
  broomOutline: '#6b4423',
} as const;

/** Annotations (text, measurement lines). */
export const ANNOTATION = {
  fill: '#1f2937',
} as const;

/** Object labels (small text tags above placed objects). */
export const LABEL = {
  fill: '#000000',
  outline: '#ffffff',
} as const;
